import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../api';

const modalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600&family=Plus+Jakarta+Sans:wght@400;500;600;700;900&display=swap');
  @keyframes spin { to { transform: rotate(360deg); } }
  .wl-modal::-webkit-scrollbar { display: none; }
  .wl-modal { -ms-overflow-style: none; scrollbar-width: none; }
  .wl-input:focus { border-color: #4E6C7E !important; box-shadow: 0 0 0 3px rgba(78,108,126,0.10) !important; }
`;

const CloseIcon = () => (
  <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
  </svg>
);
const CheckIcon = ({ color = '#10B981', size = 22 }) => (
  <svg width={size} height={size} fill="none" stroke={color} viewBox="0 0 24 24" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
  </svg>
);

const FIELDS = [
  { name: 'name',       label: 'Full Name',       placeholder: 'Jane Smith',            required: true,  type: 'text'  },
  { name: 'email',      label: 'Email',            placeholder: 'jane@university.ac.uk', required: true,  type: 'email' },
  { name: 'whatsapp',   label: 'WhatsApp Number',  placeholder: '+44 7700 900000',        required: false, type: 'tel',  hint: 'For your community invite' },
  { name: 'university', label: 'University',       placeholder: 'LSE',                   required: true,  type: 'text'  },
  { name: 'course',     label: 'Course',           placeholder: 'BSc Finance',            required: true,  type: 'text'  },
];

const inputStyle = {
  width: '100%',
  padding: '11px 14px',
  border: '1.5px solid #D9E2E6',
  borderRadius: 11,
  fontSize: 14,
  fontFamily: "Plus Jakarta Sans, sans-serif",
  color: '#0B1220',
  background: '#F4F6F7',
  outline: 'none',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  boxSizing: 'border-box',
};

export default function WaitlistModal({ open, onClose }) {
  const [form, setForm] = useState({
    name: '', email: '', whatsapp: '', university: '', course: '',
    consent: false, activelyApplying: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.university || !form.course) {
      setError('Please fill in all required fields.'); return;
    }
    if (!form.consent) {
      setError('Please agree to be contacted before submitting.'); return;
    }
    setSubmitting(true); setError('');
    try {
      await api.post('/waitlist', { ...form });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setDone(false);
      setForm({ name:'', email:'', whatsapp:'', university:'', course:'', consent:false, activelyApplying:false });
      setError('');
    }, 400);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <style>{modalStyles}</style>

          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={handleClose}
            style={{ position:'fixed', inset:0, zIndex:1000, background:'rgba(26,18,8,0.78)', backdropFilter:'blur(6px)' }}
          />

          {/* Centering wrapper */}
          <div style={{ position:'fixed', inset:0, zIndex:1001, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px', pointerEvents:'none' }}>
            <motion.div
              className="wl-modal"
              initial={{ opacity:0, scale:0.95, y:20 }}
              animate={{ opacity:1, scale:1, y:0 }}
              exit={{ opacity:0, scale:0.97, y:10 }}
              transition={{ type:'spring', stiffness:300, damping:28 }}
              style={{
                width:'100%', maxWidth:480,
                maxHeight:'92vh', overflowY:'auto',
                background:'#fff', borderRadius:24,
                boxShadow:'0 32px 80px rgba(0,0,0,0.28)',
                fontFamily: "Plus Jakarta Sans, sans-serif",
                pointerEvents:'all',
              }}
            >
              {/* ── Header ── */}
              <div style={{ background:'linear-gradient(135deg,#0B1220,#162033)', padding:'32px 32px 28px', position:'relative', overflow:'hidden', borderRadius:'24px 24px 0 0' }}>
                {/* Decorative glow */}
                <div style={{ position:'absolute', width:220, height:220, borderRadius:'50%', background:'radial-gradient(circle,#4E6C7E 0%,transparent 70%)', right:-50, top:-70, opacity:0.18, pointerEvents:'none' }} />
                {/* Close */}
                <button onClick={handleClose} style={{ position:'absolute', top:18, right:18, width:32, height:32, borderRadius:'50%', border:'none', background:'rgba(255,255,255,0.1)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'rgba(255,255,255,0.55)', zIndex:1 }}>
                  <CloseIcon />
                </button>
                {/* Title only */}
                <h2 style={{ fontFamily: "Plus Jakarta Sans, sans-serif", fontSize:'clamp(24px,4vw,32px)', fontWeight:900, color:'#fff', margin:0, letterSpacing:'-0.04em', lineHeight:1.1, position:'relative', zIndex:1 }}>
                  Join the waitlist
                </h2>
              </div>

              {/* ── Body ── */}
              <AnimatePresence mode="wait">
                {done ? (
                  <motion.div
                    key="done"
                    initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }}
                    style={{ padding:'48px 32px', display:'flex', flexDirection:'column', alignItems:'center', textAlign:'center', gap:14 }}
                  >
                    <motion.div
                      initial={{ scale:0 }} animate={{ scale:1 }}
                      transition={{ type:'spring', stiffness:300, damping:18 }}
                      style={{ width:60, height:60, borderRadius:'50%', background:'#ECFDF5', display:'flex', alignItems:'center', justifyContent:'center' }}
                    >
                      <CheckIcon />
                    </motion.div>
                    <h3 style={{ fontFamily: "Plus Jakarta Sans, sans-serif", fontSize:20, fontWeight:800, color:'#0B1220', margin:0, letterSpacing:'-0.03em' }}>You're on the list!</h3>
                    <p style={{ fontFamily: "Plus Jakarta Sans, sans-serif", fontSize:14, color:'#64748B', margin:0, lineHeight:1.65, maxWidth:300 }}>
                      We'll reach out as soon as your spot is ready. Keep an eye on your inbox.
                    </p>
                    <button onClick={handleClose} style={{ marginTop:8, padding:'11px 28px', background:'#0B1220', color:'#fff', border:'none', borderRadius:100, fontSize:14, fontWeight:600, cursor:'pointer', fontFamily: "Plus Jakarta Sans, sans-serif" }}>
                      Close
                    </button>
                  </motion.div>
                ) : (
                  <motion.div key="form" initial={{ opacity:0 }} animate={{ opacity:1 }} style={{ padding:'28px 32px 32px' }}>
                    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

                      {/* Fields */}
                      {FIELDS.map(f => (
                        <div key={f.name}>
                          <label style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, fontWeight:700, color:'#475569', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:7, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
                            {f.label}
                            {f.required && <span style={{ color:'#4E6C7E' }}>*</span>}
                            {f.hint && <span style={{ fontSize:10, color:'#94A3B8', textTransform:'none', fontWeight:400, letterSpacing:0 }}>— {f.hint}</span>}
                          </label>
                          <input
                            className="wl-input"
                            name={f.name} type={f.type}
                            value={form[f.name]} onChange={handleChange}
                            placeholder={f.placeholder}
                            style={inputStyle}
                          />
                        </div>
                      ))}

                      {/* Actively applying checkbox */}
                      <label style={{ display:'flex', alignItems:'center', gap:12, cursor:'pointer', padding:'12px 14px', background:'#E8F1F3', border:'1.5px solid #D9E2E6', borderRadius:11 }}
                        onClick={() => setForm(f => ({ ...f, activelyApplying: !f.activelyApplying }))}>
                        <div style={{ width:20, height:20, borderRadius:6, flexShrink:0, border:`2px solid ${form.activelyApplying ? '#4E6C7E' : '#C5D0D6'}`, background:form.activelyApplying ? '#4E6C7E' : '#fff', display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.15s' }}>
                          {form.activelyApplying && <CheckIcon color="#fff" size={11} />}
                        </div>
                        <span style={{ fontFamily: "Plus Jakarta Sans, sans-serif", fontSize:13, fontWeight:500, color:'#1E293B', lineHeight:1.4 }}>
                          Are you actively applying for internships?
                        </span>
                      </label>

                      {/* Data Notice */}
                      <div style={{ background:'#F4F6F7', border:'1px solid #D9E2E6', borderRadius:12, padding:'14px 16px' }}>
                        <p style={{ fontFamily: "Plus Jakarta Sans, sans-serif", fontSize:12, fontWeight:700, color:'#0B1220', margin:'0 0 6px' }}>Data Notice</p>
                        <p style={{ fontFamily: "Plus Jakarta Sans, sans-serif", fontSize:12, color:'#475569', margin:'0 0 14px', lineHeight:1.6 }}>
                          We collect your name, email, and university only to contact you about early access to Tandm. Your data will not be shared with third parties. You can request deletion at any time by emailing:{' '}
                          <span style={{ color:'#4E6C7E' }}>parasharshreyas448@gmail.com</span>
                        </p>
                        <label style={{ display:'flex', alignItems:'flex-start', gap:10, cursor:'pointer' }}
                          onClick={() => setForm(f => ({ ...f, consent: !f.consent }))}>
                          <div style={{ width:18, height:18, borderRadius:5, flexShrink:0, marginTop:1, border:`2px solid ${form.consent ? '#4E6C7E' : '#C5D0D6'}`, background:form.consent ? '#4E6C7E' : '#fff', display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.15s' }}>
                            {form.consent && <CheckIcon color="#fff" size={10} />}
                          </div>
                          <span style={{ fontFamily: "Plus Jakarta Sans, sans-serif", fontSize:12, color:'#1E293B', lineHeight:1.55 }}>
                            I agree to be contacted about early access and understand how my data will be used.{' '}
                            <span style={{ color:'#4E6C7E' }}>*</span>
                          </span>
                        </label>
                      </div>

                      {/* Error */}
                      {error && (
                        <p style={{ fontFamily: "Plus Jakarta Sans, sans-serif", fontSize:12, color:'#D94040', background:'#FFF0F0', border:'1px solid #FECACA', borderRadius:8, padding:'8px 12px', margin:0 }}>
                          {error}
                        </p>
                      )}

                      {/* Submit */}
                      <motion.button
                        whileHover={{ scale:1.02, boxShadow:'0 8px 24px rgba(78,108,126,0.35)' }}
                        whileTap={{ scale:0.97 }}
                        onClick={handleSubmit}
                        disabled={submitting}
                        style={{ width:'100%', padding:'14px', background:submitting ? '#6C969C' : 'linear-gradient(135deg,#4E6C7E,#3A5468)', color:'#fff', border:'none', borderRadius:12, fontSize:15, fontWeight:700, cursor:submitting ? 'not-allowed' : 'pointer', fontFamily: "Plus Jakarta Sans, sans-serif", marginTop:4, display:'flex', alignItems:'center', justifyContent:'center', gap:8, boxShadow:'0 4px 16px rgba(78,108,126,0.25)', letterSpacing:'-0.01em' }}
                      >
                        {submitting
                          ? <><div style={{ width:16, height:16, border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'#fff', borderRadius:'50%', animation:'spin 0.7s linear infinite' }} />Joining...</>
                          : 'Join the waitlist'
                        }
                      </motion.button>

                      <p style={{ fontFamily: "Plus Jakarta Sans, sans-serif", fontSize:11, color:'#94A3B8', textAlign:'center', margin:0 }}>
                        No spam. We'll only reach out when your spot is ready.
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
